/**
 * Mapeo de comandos de voz (parsed/raw) a intents de AI Recording.
 * Alineado con el catálogo "Qué puedo decir" en ACV (voiceCommandsCatalog.js → aiRecording).
 * Soporta pipeline de parámetros: ej. "grabar un nuevo video en pantalla completa" → startRecording + recordingMode: fullScreen.
 */

export type AIRecordingVoiceIntent =
  | 'startRecording'
  | 'stopRecording'
  | 'showChannels'
  | 'openLibrary'
  | 'openControlsBar'
  | 'closeSidebar'
  | 'openSidebar'
  | null;

export type RecordingModeParam = 'fullScreen' | 'selectArea';

export interface AIRecordingVoiceResult {
  intent: AIRecordingVoiceIntent;
  params?: { recordingMode?: RecordingModeParam };
}

function normalize(t: string): string {
  return t
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim();
}

function match(raw: string, patterns: (string | RegExp)[]): boolean {
  const n = normalize(raw);
  for (const p of patterns) {
    if (typeof p === 'string') {
      if (n.includes(normalize(p))) return true;
    } else {
      if (p.test(n) || p.test(raw)) return true;
    }
  }
  return false;
}

/** Extrae recordingMode desde parsed (backend) o desde raw (pipeline en iframe). */
function getRecordingModeFromPayload(
  raw: string,
  parsed: Record<string, unknown>
): RecordingModeParam | undefined {
  // Backend puede enviar recordingMode o fullscreen: true
  const pMode = parsed.recordingMode as string | undefined;
  if (pMode === 'fullScreen' || pMode === 'fullscreen') return 'fullScreen';
  if (pMode === 'selectArea' || pMode === 'select_area') return 'selectArea';
  if (parsed.fullscreen === true) return 'fullScreen';

  const n = normalize(raw);
  // Pantalla completa / fullscreen
  if (
    match(raw, [
      'pantalla completa',
      'en pantalla completa',
      'fullscreen',
      'full screen',
      'in fullscreen',
      'in full screen',
    ])
  ) {
    return 'fullScreen';
  }
  // Seleccionar área / select area
  if (
    match(raw, [
      'seleccionar área',
      'seleccionar area',
      'seleccionar zona',
      'select area',
      'select region',
      'grabar área',
      'grabar area',
    ])
  ) {
    return 'selectArea';
  }
  return undefined;
}

/**
 * Determina el intent y parámetros (pipeline) a partir del comando de voz (raw y/o parsed).
 * Prioridad: parsed.intent si existe; si no, matching por raw. Params (ej. recordingMode) se extraen de parsed o raw.
 */
export function getAIRecordingVoiceIntent(payload: {
  parsed?: { intent?: string; type?: string; [key: string]: unknown };
  raw?: string;
}): AIRecordingVoiceResult | null {
  const raw = typeof payload.raw === 'string' ? payload.raw : '';
  const parsed = payload.parsed && typeof payload.parsed === 'object' ? payload.parsed : {};
  const parsedIntent = parsed.intent ?? parsed.type;
  const parsedSection = String(parsed.section ?? '').toLowerCase();
  const parsedContext = String(parsed.context ?? '').toLowerCase();

  // Support staged VC actions from host (openSection/selectContext) for library flow.
  if (
    typeof parsedIntent === 'string' &&
    (parsedIntent === 'openSection' || parsedIntent === 'selectContext')
  ) {
    const librarySignals = ['library', 'biblioteca', 'collections', 'collection'];
    const hasLibrarySignal =
      librarySignals.some((k) => parsedSection.includes(k)) ||
      librarySignals.some((k) => parsedContext.includes(k));
    if (hasLibrarySignal) {
      return { intent: 'openLibrary' };
    }
  }

  const intentsMap: Record<string, AIRecordingVoiceIntent> = {
    startRecording: 'startRecording',
    start_recording: 'startRecording',
    newRecording: 'startRecording',
    new_recording: 'startRecording',
    recordVideo: 'startRecording',
    record_video: 'startRecording',
    stopRecording: 'stopRecording',
    stop_recording: 'stopRecording',
    showChannels: 'showChannels',
    show_channels: 'showChannels',
    openLibrary: 'openLibrary',
    open_library: 'openLibrary',
    library: 'openLibrary',
    myLibrary: 'openLibrary',
    my_library: 'openLibrary',
    openControlsBar: 'openControlsBar',
    open_controls_bar: 'openControlsBar',
    closeSidebar: 'closeSidebar',
    close_sidebar: 'closeSidebar',
    openSidebar: 'openSidebar',
    open_sidebar: 'openSidebar',
  };
  if (parsedIntent && typeof parsedIntent === 'string') {
    const key = parsedIntent.replace(/-/g, '_');
    if (intentsMap[key] !== undefined) {
      const intent = intentsMap[key];
      const params =
        intent === 'startRecording'
          ? { recordingMode: getRecordingModeFromPayload(raw, parsed as Record<string, unknown>) }
          : undefined;
      return { intent, params: params?.recordingMode ? { recordingMode: params.recordingMode } : undefined };
    }
  }

  if (!raw) return null;

  // ── Iniciar grabación (incl. "grabar un nuevo video en pantalla completa", "record a new video in fullscreen")
  const startPatterns = [
    'graba un nuevo video',
    'graba un nuevo vídeo',
    'grabar un nuevo video',
    'grabar un nuevo vídeo',
    'iniciar grabación',
    'iniciar grabacion',
    'empezar a grabar',
    'nueva grabación',
    'nueva grabacion',
    'record a new video',
    'start recording',
    'record video',
    'grabar video',
    'graba video',
    'comenzar grabación',
    'begin recording',
  ];
  if (match(raw, startPatterns)) {
    const recordingMode = getRecordingModeFromPayload(raw, parsed as Record<string, unknown>);
    return {
      intent: 'startRecording',
      params: recordingMode ? { recordingMode } : undefined,
    };
  }

  // ── Detener grabación
  if (
    match(raw, [
      'detener grabación',
      'detener grabacion',
      'parar grabación',
      'parar grabacion',
      'stop recording',
      'stop record',
    ])
  ) {
    return { intent: 'stopRecording' };
  }

  // ── Ver canales
  if (match(raw, ['ver canales', 'show channels', 'ver canal', 'listar canales', 'open channels'])) {
    return { intent: 'showChannels' };
  }

  // ── Abrir biblioteca / mi biblioteca
  if (
    match(raw, [
      'mi biblioteca',
      'abrir mi biblioteca',
      'abre mi biblioteca',
      'ir a mi biblioteca',
      'biblioteca',
      'library',
      'my library',
      'open my library',
      'open library',
      'go to library',
    ])
  ) {
    return { intent: 'openLibrary' };
  }

  // ── Abrir barra de controles
  if (
    match(raw, [
      'abrir barra de controles',
      'mostrar controles',
      'open controls',
      'show controls',
      'barra de controles',
    ])
  ) {
    return { intent: 'openControlsBar' };
  }

  // ── Cerrar panel lateral
  if (
    match(raw, [
      'cerrar panel lateral',
      'cerrar panel',
      'cerrar sidebar',
      'close panel',
      'close sidebar',
      'ocultar panel',
      'hide sidebar',
    ])
  ) {
    return { intent: 'closeSidebar' };
  }

  // ── Abrir panel lateral
  if (
    match(raw, [
      'abrir panel lateral',
      'abrir panel',
      'abrir sidebar',
      'open panel',
      'open sidebar',
      'mostrar panel',
      'show sidebar',
    ])
  ) {
    return { intent: 'openSidebar' };
  }

  return null;
}
